import { useState } from "react";
import { useGymPendingReceipts, useUpdateReceiptStatus } from "@/hooks/payments/usePaymentReceipts";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Check, X, Eye, Clock, Loader2 } from "lucide-react";
import { ReservationReceiptModal } from "@/components/reservations/ReservationReceiptModal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PaymentForm } from "@/components/settings/admin/stats/payments/PaymentForm";
import { useQueryClient } from "@tanstack/react-query";

interface PendingReceiptsManagerProps {
  gymId: string | null;
}

export const PendingReceiptsManager = ({ gymId }: PendingReceiptsManagerProps) => {
  const { data: receipts, isLoading } = useGymPendingReceipts(gymId);
  const updateStatus = useUpdateReceiptStatus();
  const queryClient = useQueryClient();

  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  if (isLoading) return null;
  if (!receipts?.length) return null;

  const handleReject = async () => {
    if (!rejectingId || !currentUserId) return;
    await updateStatus.mutateAsync({
      id: rejectingId,
      status: "rejected",
      rejection_reason: rejectionReason || undefined,
      reviewed_by: currentUserId,
    });
    setRejectingId(null);
    setRejectionReason("");
  };

  const handleApproved = (receiptId: string, reviewerId: string) => {
    updateStatus.mutate({
      id: receiptId,
      status: "approved",
      reviewed_by: reviewerId,
    });
  };

  return (
    <div className="bg-card border rounded-xl p-4 sm:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Clock className="h-5 w-5 text-yellow-500" />
        <h3 className="font-semibold text-lg">Comprobantes pendientes ({receipts.length})</h3>
      </div>

      <div className="space-y-3">
        {receipts.map((receipt) => (
          <ReceiptRow
            key={receipt.id}
            receipt={receipt}
            gymId={gymId}
            onReject={(id, userId) => {
              setRejectingId(id);
              setCurrentUserId(userId);
            }}
            onApproved={handleApproved}
          />
        ))}
      </div>

      <AlertDialog open={!!rejectingId} onOpenChange={(open) => { if (!open) { setRejectingId(null); setRejectionReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Rechazar comprobante?</AlertDialogTitle>
            <AlertDialogDescription>
              Opcionalmente podés indicar el motivo del rechazo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Motivo del rechazo (opcional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Rechazar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

interface ReceiptRowProps {
  receipt: any;
  gymId: string | null;
  onReject: (receiptId: string, reviewerId: string) => void;
  onApproved: (receiptId: string, reviewerId: string) => void;
}

const ReceiptRow = ({ receipt, gymId, onReject, onApproved }: ReceiptRowProps) => {
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const userName = [receipt.user_first_name, receipt.user_last_name].filter(Boolean).join(" ") || receipt.user_username || "Sin nombre";

  const handlePaymentRegistered = () => {
    import("@/integrations/supabase/client").then(({ supabase }) => {
      supabase.auth.getUser().then(({ data }) => {
        if (data.user) {
          onApproved(receipt.id, data.user.id);
        }
      });
    });
  };

  return (
    <div className="border rounded-lg p-3 sm:p-4 space-y-2">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="space-y-1">
          <p className="font-medium text-sm">{userName}</p>
          {receipt.user_dni && (
            <p className="text-xs text-muted-foreground">DNI: {receipt.user_dni}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Categoría: <span className="font-medium text-foreground">{receipt.category_name || "Sin categoría"}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {format(new Date(receipt.created_at), "dd/MM/yyyy HH:mm", { locale: es })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setReceiptModalOpen(true)}
          >
            <Eye className="h-3.5 w-3.5 mr-1" />
            Ver comprobante
          </Button>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <PaymentForm
          user={{
            id: receipt.user_id,
            first_name: receipt.user_first_name,
            last_name: receipt.user_last_name,
            username: receipt.user_username,
          } as any}
          gymId={gymId}
          onPaymentRegistered={handlePaymentRegistered}
        />
        <Button
          variant="outline"
          size="sm"
          className="text-destructive border-destructive/30 hover:bg-destructive/10"
          onClick={async () => {
            const { supabase } = await import("@/integrations/supabase/client");
            const { data } = await supabase.auth.getUser();
            if (data.user) onReject(receipt.id, data.user.id);
          }}
        >
          <X className="h-4 w-4 mr-1" />
          Rechazar
        </Button>
      </div>
      <ReservationReceiptModal
        isOpen={receiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        receiptUrl={receipt.receipt_url}
        reservationTitle={userName}
      />
    </div>
  );
};
